/**
 * Olympus Offline Storage Service
 * Verwendet IndexedDB für lokale Datenspeicherung
 */

const DB_NAME = 'olympus-offline';
const DB_VERSION = 1;

// Store-Namen
const STORES = {
  KLIENTEN: 'klienten',
  AUFGABEN: 'aufgaben',
  FRISTEN: 'fristen',
  SETTINGS: 'settings',
  CACHE_META: 'cache_meta'
};

class OfflineStorageService {
  constructor() {
    this.db = null;
    this.isReady = false;
    this.initPromise = this.init();
  }

  // ==================== INITIALISIERUNG ====================

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[OfflineStorage] Fehler beim Öffnen der Datenbank:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isReady = true;
        console.log('[OfflineStorage] Datenbank erfolgreich geöffnet');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        console.log('[OfflineStorage] Datenbank wird erstellt/aktualisiert');

        // Klienten Store
        if (!db.objectStoreNames.contains(STORES.KLIENTEN)) {
          const klientenStore = db.createObjectStore(STORES.KLIENTEN, { keyPath: 'id' });
          klientenStore.createIndex('nachname', 'nachname', { unique: false });
          klientenStore.createIndex('lastViewed', 'lastViewed', { unique: false });
        }

        // Aufgaben Store
        if (!db.objectStoreNames.contains(STORES.AUFGABEN)) {
          const aufgabenStore = db.createObjectStore(STORES.AUFGABEN, { keyPath: 'id' });
          aufgabenStore.createIndex('klient_id', 'klient_id', { unique: false });
          aufgabenStore.createIndex('faellig_am', 'faellig_am', { unique: false });
        }

        // Fristen Store
        if (!db.objectStoreNames.contains(STORES.FRISTEN)) {
          const fristenStore = db.createObjectStore(STORES.FRISTEN, { keyPath: 'id' });
          fristenStore.createIndex('klient_id', 'klient_id', { unique: false });
          fristenStore.createIndex('frist_datum', 'frist_datum', { unique: false });
        }

        // Settings Store
        if (!db.objectStoreNames.contains(STORES.SETTINGS)) {
          db.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
        }

        // Cache Meta Store (für Sync-Tracking)
        if (!db.objectStoreNames.contains(STORES.CACHE_META)) {
          db.createObjectStore(STORES.CACHE_META, { keyPath: 'key' });
        }
      };
    });
  }

  async ensureReady() {
    if (!this.isReady) {
      await this.initPromise;
    }
  }

  // ==================== GENERISCHE METHODEN ====================

  async getAll(storeName) {
    await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async get(storeName, id) {
    await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async put(storeName, data) {
    await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async putMany(storeName, items) {
    await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      items.forEach(item => store.put(item));

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async delete(storeName, id) {
    await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clear(storeName) {
    await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== KLIENTEN METHODEN ====================

  async saveKlienten(klienten) {
    // Timestamp hinzufügen
    const timestampedKlienten = klienten.map(k => ({
      ...k,
      _cachedAt: new Date().toISOString()
    }));
    
    await this.putMany(STORES.KLIENTEN, timestampedKlienten);
    await this.updateCacheMeta('klienten');
    console.log(`[OfflineStorage] ${klienten.length} Klienten gespeichert`);
  }

  async getKlienten() {
    return this.getAll(STORES.KLIENTEN);
  }

  async getKlient(id) {
    return this.get(STORES.KLIENTEN, id);
  }

  async markKlientAsViewed(id) {
    const klient = await this.getKlient(id);
    if (klient) {
      klient.lastViewed = new Date().toISOString();
      await this.put(STORES.KLIENTEN, klient);
    }
  }

  async getRecentlyViewedKlienten(limit = 10) {
    const klienten = await this.getKlienten();
    return klienten
      .filter(k => k.lastViewed)
      .sort((a, b) => new Date(b.lastViewed) - new Date(a.lastViewed))
      .slice(0, limit);
  }

  // ==================== AUFGABEN METHODEN ====================

  async saveAufgaben(aufgaben) {
    const timestampedAufgaben = aufgaben.map(a => ({
      ...a,
      _cachedAt: new Date().toISOString()
    }));
    
    await this.putMany(STORES.AUFGABEN, timestampedAufgaben);
    await this.updateCacheMeta('aufgaben');
    console.log(`[OfflineStorage] ${aufgaben.length} Aufgaben gespeichert`);
  }

  async getAufgaben() {
    return this.getAll(STORES.AUFGABEN);
  }

  async getAufgabenByKlient(klientId) {
    const aufgaben = await this.getAufgaben();
    return aufgaben.filter(a => a.klient_id === klientId);
  }

  // ==================== FRISTEN METHODEN ====================

  async saveFristen(fristen) {
    const timestampedFristen = fristen.map(f => ({
      ...f,
      _cachedAt: new Date().toISOString()
    }));
    
    await this.putMany(STORES.FRISTEN, timestampedFristen);
    await this.updateCacheMeta('fristen');
    console.log(`[OfflineStorage] ${fristen.length} Fristen gespeichert`);
  }

  async getFristen() {
    return this.getAll(STORES.FRISTEN);
  }

  // ==================== CACHE META METHODEN ====================

  async updateCacheMeta(key) {
    await this.put(STORES.CACHE_META, {
      key,
      lastSync: new Date().toISOString()
    });
  }

  async getCacheMeta(key) {
    return this.get(STORES.CACHE_META, key);
  }

  async getLastSyncTime(key) {
    const meta = await this.getCacheMeta(key);
    return meta?.lastSync || null;
  }

  // ==================== SETTINGS ====================

  async setSetting(key, value) {
    await this.put(STORES.SETTINGS, { key, value });
  }

  async getSetting(key) {
    const result = await this.get(STORES.SETTINGS, key);
    return result?.value;
  }

  // ==================== SYNC STATUS ====================

  async getSyncStatus() {
    const [klienten, aufgaben, fristen] = await Promise.all([
      this.getCacheMeta('klienten'),
      this.getCacheMeta('aufgaben'),
      this.getCacheMeta('fristen')
    ]);

    const [klientenCount, aufgabenCount, fristenCount] = await Promise.all([
      this.getKlienten().then(k => k.length),
      this.getAufgaben().then(a => a.length),
      this.getFristen().then(f => f.length)
    ]);

    return {
      klienten: {
        lastSync: klienten?.lastSync,
        count: klientenCount
      },
      aufgaben: {
        lastSync: aufgaben?.lastSync,
        count: aufgabenCount
      },
      fristen: {
        lastSync: fristen?.lastSync,
        count: fristenCount
      },
      isOnline: navigator.onLine
    };
  }

  // ==================== CLEANUP ====================

  async clearAllData() {
    await Promise.all([
      this.clear(STORES.KLIENTEN),
      this.clear(STORES.AUFGABEN),
      this.clear(STORES.FRISTEN),
      this.clear(STORES.CACHE_META)
    ]);
    console.log('[OfflineStorage] Alle Daten gelöscht');
  }
}

// Singleton-Instanz exportieren
const offlineStorage = new OfflineStorageService();
export default offlineStorage;
export { STORES };
